import { AnimatedContentLoader, Empty, SingleColumnLayout } from '@components'
import { ReportRenderFormat, ReportType } from '@constants'
import { isVATObliged, useBusinessContext, useDateRangeContext } from '@context'
import { useDebugMode, useOpenLucaWithPrompt, useLucaViewContext } from '@hooks'
import {
  AccountingReportJson,
  BalanceSheetReconciliationJson,
  EuVatContactJson,
  fetchPeriods,
  IDateRangeKey,
  InteractiveReportKey,
  JournalJson,
  LedgerJson,
  ReportComparisonMode,
  ReportRequestBody,
  useInteractiveReport,
  useTags,
  isReportDataForKey,
  VatJson
} from '@query'
import { downloadCsvReport, downloadDocxReport, generateReport } from '@utils/generateReport'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AccountingReportTree } from './AccountingReportTree'
import type { ColumnControls } from './ColumnHeaderMenu'
import { ColumnTitleDialog } from './ColumnTitleDialog'
import { EuSalesView } from './EuSalesView'
import { JournalView } from './JournalView'
import { LedgerView } from './LedgerView'
import { PageInner, ReportViewport } from './interactive.styled'
import {
  buildReportColumns,
  canShowComparison,
  columnFromFilter,
  createInsertedColumn,
  MAX_PERIOD_COLUMNS,
  migrateColumnsForReport,
  resolveColumn,
  resolveColumns,
  resolveDrillWindow,
  supportsMultipleColumns,
  usesAsOfPeriod,
  type FiscalYearContext,
  type PeriodColumn
} from './periodUtils'
import { ReportPeriodDialog, type PeriodDialogMode } from './ReportPeriodDialog'
import {
  buildOptimisticAccountingReport,
  needsOptimisticColumnLayout,
  resolveChangeColumnLabel
} from './optimisticReportData'
import { ReportToolbar } from './ReportToolbar'
import { SpecificationsView } from './SpecificationsView'
import { VatView } from './VatView'
import {
  emptyReportExpansion,
  getReportExpansion,
  useInteractiveReportsSession,
  type ReportExpansion
} from './interactiveReportsSession'

const REPORTS_WITHOUT_ENTRY_LINES = new Set<InteractiveReportKey>([
  'ledger',
  'journal',
  'balance_sheet_specifications',
  'vat'
])

const LUCA_HINTS: Record<InteractiveReportKey, string[]> = {
  balance_sheet: [
    'reports.interactive.lucaHints.bs1',
    'reports.interactive.lucaHints.bs2',
    'reports.interactive.lucaHints.bs3'
  ],
  income_statement: [
    'reports.interactive.lucaHints.is1',
    'reports.interactive.lucaHints.is2',
    'reports.interactive.lucaHints.is3'
  ],
  ledger: ['reports.interactive.lucaHints.ledger1', 'reports.interactive.lucaHints.ledger2'],
  journal: ['reports.interactive.lucaHints.journal1', 'reports.interactive.lucaHints.journal2'],
  balance_sheet_specifications: [
    'reports.interactive.lucaHints.spec1',
    'reports.interactive.lucaHints.spec2'
  ],
  vat: ['reports.interactive.lucaHints.vat1', 'reports.interactive.lucaHints.vat2'],
  eu_vat: ['reports.interactive.lucaHints.eu1', 'reports.interactive.lucaHints.eu2']
}

const formatDisplayDate = (value: string) => dayjs(value).format('D.M.YYYY')

const getMultiColumnReportLabels = (
  reportKey: InteractiveReportKey,
  t: (key: string) => string
) => {
  const isBalanceSheet = reportKey === 'balance_sheet'
  return {
    title: t(
      isBalanceSheet ? 'constants.reportType.balanceSheet' : 'constants.reportType.incomeStatement'
    ),
    footNote: t(
      isBalanceSheet
        ? 'reports.interactive.footNote.balanceSheet'
        : 'reports.interactive.footNote.incomeStatement'
    )
  }
}

export const InteractiveReportsPage: React.FC = () => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const { selectedRange, options: periodFilters, isReady: isDateRangeReady } = useDateRangeContext()
  const vatObliged = isVATObliged(business)
  const [debugModeOn] = useDebugMode()
  const openLucaWithPrompt = useOpenLucaWithPrompt()
  const { session, setSession, consumeRestoredSession } = useInteractiveReportsSession(slug)
  const { reportKey, tagIds, columns, showComparison, comparisonMode } = session
  const expansion = getReportExpansion(session, reportKey)

  const [periodDialogColumnId, setPeriodDialogColumnId] = useState<string | null>(null)
  const [renameColumnId, setRenameColumnId] = useState<string | null>(null)
  const [expandAllToken, setExpandAllToken] = useState(0)
  const [collapseAllToken, setCollapseAllToken] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const isInitialReportKeyRef = useRef(true)
  const reportKeyRef = useRef(reportKey)
  reportKeyRef.current = reportKey
  const previousRangeFingerprintRef = useRef<string | null>(null)

  const setReportKey = (key: InteractiveReportKey) =>
    setSession(current => ({ ...current, reportKey: key }))
  const setTagIds = (ids: number[]) => setSession(current => ({ ...current, tagIds: ids }))
  const setColumns = (value: PeriodColumn[] | ((previous: PeriodColumn[]) => PeriodColumn[])) =>
    setSession(current => ({
      ...current,
      columns: typeof value === 'function' ? value(current.columns) : value
    }))
  const setShowComparison = (value: boolean) =>
    setSession(current => ({ ...current, showComparison: value }))
  const setComparisonMode = (value: ReportComparisonMode) =>
    setSession(current => ({ ...current, comparisonMode: value }))

  const onExpansionChange = useCallback(
    (patch: Partial<ReportExpansion>) => {
      setSession(current => ({
        ...current,
        expansion: {
          ...current.expansion,
          [current.reportKey]: {
            ...emptyReportExpansion(),
            ...current.expansion[current.reportKey],
            ...patch
          }
        }
      }))
    },
    [setSession]
  )

  // One-way sync from the global sidebar period, matching legacy reports: reset to a
  // single column. Skip the first application when this visit restored session state.
  useEffect(() => {
    if (!selectedRange?.key) return
    const fingerprint = `${selectedRange.key}|${selectedRange.date_from}|${selectedRange.date_to}`
    const previous = previousRangeFingerprintRef.current
    previousRangeFingerprintRef.current = fingerprint
    if (previous !== null && previous === fingerprint) return
    if (previous === null && consumeRestoredSession()) return
    setColumns(current => [columnFromFilter(selectedRange, reportKeyRef.current, current[0]?.id)])
  }, [selectedRange, consumeRestoredSession])

  // Preserve stamped dates and comparison columns when switching report type.
  useEffect(() => {
    if (isInitialReportKeyRef.current) {
      isInitialReportKeyRef.current = false
      return
    }
    setColumns(prev => migrateColumnsForReport(prev, reportKey))
  }, [reportKey])

  useEffect(() => {
    if (!debugModeOn && reportKey === 'balance_sheet_specifications') {
      setReportKey('balance_sheet')
    }
  }, [debugModeOn, reportKey])

  const { data: periodsData } = useQuery({
    queryKey: [slug, 'periods', 'report-fiscal-years'],
    queryFn: () => fetchPeriods({ slug }),
    enabled: Boolean(slug)
  })

  const fiscalYearContext = useMemo<FiscalYearContext>(() => {
    const periods = [...(periodsData?.results || [])].sort((a, b) =>
      a.start_date.localeCompare(b.start_date)
    )
    const activeIndex = periods.findIndex(period => period.is_active)
    return {
      years: periods.map(({ start_date, end_date }) => ({ start_date, end_date })),
      activeIndex: activeIndex >= 0 ? activeIndex : periods.length - 1
    }
  }, [periodsData])

  const multiColumn = supportsMultipleColumns(reportKey)

  const resolvedColumns = useMemo(() => resolveColumns(reportKey, columns), [reportKey, columns])
  const primaryPeriod = resolvedColumns[0]
  const rangeText = primaryPeriod
    ? reportKey === 'balance_sheet'
      ? formatDisplayDate(primaryPeriod.dateTo)
      : `${formatDisplayDate(primaryPeriod.dateFrom)} – ${formatDisplayDate(primaryPeriod.dateTo)}`
    : ''
  // Headers and labels keep `primaryPeriod`; only the account drill widens.
  const drillPeriod = useMemo(
    () => (columns[0] ? resolveDrillWindow(reportKey, columns[0], fiscalYearContext) : undefined),
    [reportKey, columns, fiscalYearContext]
  )

  const comparisonEnabled = multiColumn && showComparison && canShowComparison(columns.length)

  const requestBody: ReportRequestBody = useMemo(() => {
    const body: ReportRequestBody = {
      columns: buildReportColumns({ reportKey, columns }),
      extend_accounts: multiColumn,
      append_comparison_columns: comparisonEnabled,
      tag_ids: tagIds.length ? tagIds : undefined,
      include_entry_lines: REPORTS_WITHOUT_ENTRY_LINES.has(reportKey) ? false : undefined
    }
    if (comparisonEnabled) {
      body.comparison_mode = comparisonMode
    }
    return body
  }, [reportKey, columns, multiColumn, comparisonEnabled, comparisonMode, tagIds])

  const reportOptions = useMemo(() => {
    const options: { key: InteractiveReportKey; label: string }[] = [
      { key: 'balance_sheet', label: t('constants.reportType.balanceSheet') },
      { key: 'income_statement', label: t('constants.reportType.incomeStatement') },
      { key: 'ledger', label: t('constants.reportType.ledger') },
      { key: 'journal', label: t('constants.reportType.journal') }
    ]
    if (debugModeOn) {
      options.push({
        key: 'balance_sheet_specifications',
        label: t('constants.reportType.balanceSheetSpecifications')
      })
    }
    if (vatObliged) {
      options.push(
        { key: 'vat', label: t('constants.reportType.vatReport') },
        { key: 'eu_vat', label: t('constants.reportType.euVatContactReport') }
      )
    }
    return options
  }, [t, debugModeOn, vatObliged])

  const { data, isFetching, isError, isLoading, isPlaceholderData } = useInteractiveReport({
    slug,
    reportKey,
    body: requestBody,
    enabled: isDateRangeReady
  })

  const canShowCurrentReport = Boolean(data && isReportDataForKey(data, reportKey))
  const showReportLoader = !canShowCurrentReport && (isLoading || isFetching)
  const isRefreshing = isFetching && canShowCurrentReport

  const lastFetchedSnapshotRef = useRef<{
    columns: PeriodColumn[]
    comparisonEnabled: boolean
    comparisonMode: ReportComparisonMode
    data: AccountingReportJson
  } | null>(null)

  useEffect(() => {
    if (!isFetching && data && multiColumn) {
      lastFetchedSnapshotRef.current = {
        columns,
        comparisonEnabled,
        comparisonMode,
        data: data as AccountingReportJson
      }
    }
  }, [isFetching, data, columns, comparisonEnabled, comparisonMode, multiColumn])

  const changeColumnLabel = useMemo(() => {
    const fallback = t('reports.interactive.changeColumn')
    const snapshot = lastFetchedSnapshotRef.current
    if (snapshot) {
      return resolveChangeColumnLabel(snapshot.data.labels, snapshot.columns.length, fallback)
    }
    if (!multiColumn || !data || !isReportDataForKey(data, reportKey)) {
      return fallback
    }
    return resolveChangeColumnLabel((data as AccountingReportJson).labels, columns.length, fallback)
  }, [data, columns.length, multiColumn, reportKey, t])

  const accountingDisplayData = useMemo(() => {
    if (!data || !multiColumn) return null

    const report = data as AccountingReportJson
    const snapshot = lastFetchedSnapshotRef.current

    if (
      !isRefreshing ||
      !snapshot ||
      !needsOptimisticColumnLayout(
        snapshot.columns,
        snapshot.comparisonEnabled,
        snapshot.comparisonMode,
        columns,
        comparisonEnabled,
        comparisonMode
      )
    ) {
      return report
    }

    return buildOptimisticAccountingReport({
      data: snapshot.data,
      lastColumns: snapshot.columns,
      lastComparisonEnabled: snapshot.comparisonEnabled,
      lastComparisonMode: snapshot.comparisonMode,
      currentColumns: columns,
      currentResolvedColumns: resolvedColumns,
      currentComparisonEnabled: comparisonEnabled,
      currentComparisonMode: comparisonMode,
      changeLabel: changeColumnLabel
    })
  }, [
    data,
    multiColumn,
    isRefreshing,
    columns,
    resolvedColumns,
    comparisonEnabled,
    comparisonMode,
    changeColumnLabel
  ])

  const periodChipLabel =
    columns[0]?.preset && columns[0].preset !== 'custom'
      ? t(`components.dateRangeFilter.${columns[0].preset}`)
      : rangeText

  const reportLabel = reportOptions.find(option => option.key === reportKey)?.label
  const { data: selectedTags } = useTags({ tagIds })
  const tagLabel = useMemo(() => {
    if (!tagIds.length) return undefined

    const names = selectedTags?.results?.map(tag => tag.name.trim()).filter(Boolean) ?? []

    if (names.length === tagIds.length) {
      return names.length === 1 ? `tag: ${names[0]}` : `tags: ${names.join(', ')}`
    }

    return t('reports.interactive.tagCount', { count: tagIds.length })
  }, [tagIds, selectedTags, t])
  const lucaViewContext = useMemo(
    () => ({
      entityType: 'report',
      entityId: reportKey,
      entityLabel: [reportLabel, rangeText, tagLabel].filter(Boolean).join(' · ')
    }),
    [reportKey, reportLabel, rangeText, tagLabel]
  )
  useLucaViewContext(lucaViewContext)

  const periodDialogMode: PeriodDialogMode = usesAsOfPeriod(reportKey) ? 'asOf' : 'range'
  const periodDialogColumn = columns.find(column => column.id === periodDialogColumnId)
  const periodDialogPeriod = periodDialogColumn
    ? resolveColumn(reportKey, periodDialogColumn)
    : undefined

  const renameColumn = columns.find(column => column.id === renameColumnId)
  // Placeholder shows the generated label, so resolve without the current title.
  const renamePlaceholder = renameColumn
    ? resolveColumn(reportKey, { ...renameColumn, title: undefined }).label
    : ''

  const lucaHints = (LUCA_HINTS[reportKey] || []).map(key => t(key))

  const handlePresetSelect = (columnId: string, preset: IDateRangeKey) => {
    if (preset === 'custom') {
      setPeriodDialogColumnId(columnId)
      return
    }
    const filter = periodFilters.find(option => option.key === preset)
    if (!filter) return

    setColumns(previous =>
      previous.map(column =>
        column.id === columnId ? columnFromFilter(filter, reportKey, column.id) : column
      )
    )
  }

  const handleInsertColumn = (columnId: string, direction: 'before' | 'after') => {
    if (columns.length >= MAX_PERIOD_COLUMNS) return
    const anchorIndex = columns.findIndex(column => column.id === columnId)
    if (anchorIndex < 0) return

    const next = [...columns]
    const inserted = createInsertedColumn(reportKey, periodFilters)
    next.splice(direction === 'before' ? anchorIndex : anchorIndex + 1, 0, inserted)
    setColumns(next)
  }

  const handleAppendColumn = () => {
    if (!columns.length) return
    handleInsertColumn(columns[columns.length - 1].id, 'after')
  }

  const handleRemoveColumn = (columnId: string) => {
    if (columns.length <= 1) return
    setColumns(previous => previous.filter(column => column.id !== columnId))
  }

  const handlePeriodApply = (value: { asOf?: string; dateFrom?: string; dateTo?: string }) => {
    if (!periodDialogColumnId) return
    setColumns(previous =>
      previous.map(column => {
        if (column.id !== periodDialogColumnId) return column
        if (usesAsOfPeriod(reportKey)) {
          const asOf = value.asOf
          return {
            ...column,
            preset: 'custom',
            asOf,
            dateFrom: asOf,
            dateTo: asOf
          }
        }
        return {
          ...column,
          preset: 'custom',
          asOf: undefined,
          dateFrom: value.dateFrom,
          dateTo: value.dateTo
        }
      })
    )
    setPeriodDialogColumnId(null)
  }

  const handleTitleApply = (title: string) => {
    if (!renameColumnId) return
    setColumns(previous =>
      previous.map(column =>
        column.id === renameColumnId ? { ...column, title: title || undefined } : column
      )
    )
    setRenameColumnId(null)
  }

  const columnControls: ColumnControls = {
    reportKey,
    columns,
    periodFilters,
    onPresetSelect: handlePresetSelect,
    onInsert: handleInsertColumn,
    onAppend: handleAppendColumn,
    onRename: setRenameColumnId,
    onRemove: handleRemoveColumn
  }

  const expansionProps = { expansion, onExpansionChange }

  const handleExport = async (format: ReportRenderFormat, type: ReportType) => {
    setIsExporting(true)
    try {
      const payload = {
        business_id: slug,
        type,
        columns: requestBody.columns,
        extend_accounts: requestBody.extend_accounts,
        append_comparison_columns: requestBody.append_comparison_columns,
        tag_ids: requestBody.tag_ids,
        ...(requestBody.comparison_mode ? { comparison_mode: requestBody.comparison_mode } : {})
      }
      if (format === ReportRenderFormat.CSV) {
        await downloadCsvReport(payload)
      } else if (format === ReportRenderFormat.DOCX) {
        await downloadDocxReport(payload)
      } else {
        await generateReport(payload)
      }
    } finally {
      setIsExporting(false)
    }
  }

  const renderReport = () => {
    if (isError && !data) {
      return <Empty text={t('reports.interactive.loadError')} />
    }

    if (!data) {
      return null
    }

    if (!isReportDataForKey(data, reportKey)) {
      return null
    }

    if (multiColumn && accountingDisplayData) {
      const { title, footNote } = getMultiColumnReportLabels(reportKey, t)
      return (
        <AccountingReportTree
          slug={slug}
          data={accountingDisplayData}
          drillPeriod={drillPeriod ?? primaryPeriod}
          columnControls={columnControls}
          tagIds={tagIds}
          {...expansionProps}
          expandAllToken={expandAllToken}
          collapseAllToken={collapseAllToken}
          isRefreshing={isRefreshing}
          isStale={isPlaceholderData}
          title={title}
          footNote={footNote}
        />
      )
    }

    if (reportKey === 'ledger') {
      return (
        <LedgerView
          slug={slug}
          reportBody={requestBody}
          data={data as LedgerJson}
          {...expansionProps}
          expandAllToken={expandAllToken}
          collapseAllToken={collapseAllToken}
          isRefreshing={isRefreshing}
          isStale={isPlaceholderData}
          hasTagFilter={tagIds.length > 0}
        />
      )
    }

    if (reportKey === 'journal') {
      return (
        <JournalView
          slug={slug}
          reportBody={requestBody}
          data={data as JournalJson}
          {...expansionProps}
          expandAllToken={expandAllToken}
          collapseAllToken={collapseAllToken}
          isRefreshing={isRefreshing}
          isStale={isPlaceholderData}
          hasTagFilter={tagIds.length > 0}
        />
      )
    }

    if (reportKey === 'balance_sheet_specifications') {
      return (
        <SpecificationsView
          slug={slug}
          reportBody={requestBody}
          data={data as BalanceSheetReconciliationJson}
          {...expansionProps}
          expandAllToken={expandAllToken}
          collapseAllToken={collapseAllToken}
          isRefreshing={isRefreshing}
          isStale={isPlaceholderData}
        />
      )
    }

    if (reportKey === 'vat') {
      return (
        <VatView
          slug={slug}
          reportBody={requestBody}
          data={data as VatJson}
          {...expansionProps}
          expandAllToken={expandAllToken}
          collapseAllToken={collapseAllToken}
          isRefreshing={isRefreshing}
          isStale={isPlaceholderData}
          onOpenEuSalesReport={() => setReportKey('eu_vat')}
        />
      )
    }

    return (
      <EuSalesView
        data={data as EuVatContactJson}
        business={business}
        periodLabel={primaryPeriod?.label}
        isStale={isPlaceholderData}
      />
    )
  }

  return (
    <SingleColumnLayout header={t('reports.title')}>
      <PageInner>
        <ReportToolbar
          reportOptions={reportOptions}
          reportKey={reportKey}
          onReportChange={setReportKey}
          hasTags={Boolean(business?.has_tags)}
          tagIds={tagIds}
          onTagIdsChange={setTagIds}
          showPeriodChip={!multiColumn && isDateRangeReady}
          periodChipLabel={periodChipLabel}
          periodFilters={periodFilters}
          selectedPeriodKey={columns[0]?.preset}
          onPeriodFilterSelect={preset => handlePresetSelect(columns[0].id, preset)}
          showComparison={comparisonEnabled}
          onShowComparisonChange={setShowComparison}
          comparisonMode={comparisonMode}
          onComparisonModeChange={setComparisonMode}
          comparisonAvailable={multiColumn}
          comparisonDisabled={!canShowComparison(columns.length)}
          comparisonDisabledHint={
            columns.length < 2
              ? t('reports.interactive.comparisonNeedsTwoColumns')
              : t('reports.interactive.comparisonMaxColumns')
          }
          onExpandAll={() => setExpandAllToken(token => token + 1)}
          onCollapseAll={() => setCollapseAllToken(token => token + 1)}
          onExport={handleExport}
          isExporting={isExporting}
          lucaHints={lucaHints}
          onLucaHint={openLucaWithPrompt}
        />

        <ReportPeriodDialog
          isOpen={Boolean(periodDialogColumn)}
          mode={periodDialogMode}
          initialAsOf={periodDialogPeriod?.dateTo}
          initialFrom={periodDialogPeriod?.dateFrom}
          initialTo={periodDialogPeriod?.dateTo}
          onClose={() => setPeriodDialogColumnId(null)}
          onApply={handlePeriodApply}
        />

        <ColumnTitleDialog
          isOpen={Boolean(renameColumn)}
          initialTitle={renameColumn?.title}
          placeholder={renamePlaceholder}
          onClose={() => setRenameColumnId(null)}
          onApply={handleTitleApply}
        />

        <ReportViewport>
          <AnimatedContentLoader isLoading={showReportLoader} contentDisplay="flex">
            {renderReport()}
          </AnimatedContentLoader>
        </ReportViewport>
      </PageInner>
    </SingleColumnLayout>
  )
}
